import type { ComponentType } from 'react';
import dynamic from 'next/dynamic';
import type { LayoutProps } from './types';

// Cada perfil usa exactamente UN layout — cargar los 9 de forma estática enviaba ~79KB de
// JS de layouts que nunca se renderizan en cada visita. next/dynamic los separa en chunks
// independientes; solo se descarga el que corresponde al perfil visitado.
const ClassicLayout = dynamic(() => import('./ClassicLayout'));
const SidebarLayout = dynamic(() => import('./SidebarLayout'));
const CardStackLayout = dynamic(() => import('./CardStackLayout'));
const HeroBannerLayout = dynamic(() => import('./HeroBannerLayout'));
const GridLayout = dynamic(() => import('./GridLayout'));
const BubbleLayout = dynamic(() => import('./BubbleLayout'));
const SplitLayout = dynamic(() => import('./SplitLayout'));
const MinimalLayout = dynamic(() => import('./MinimalLayout'));
const BeatsLayout = dynamic(() => import('./BeatsLayout'));
const Crt80sLayout = dynamic(() => import('./Crt80sLayout'));
const HalloweenLightLayout = dynamic(() => import('./HalloweenLightLayout'));
const HalloweenDarkLayout = dynamic(() => import('./HalloweenDarkLayout'));
const SpectralStripesLayout = dynamic(() => import('./SpectralStripesLayout'));
const GothicPortraitLayout = dynamic(() => import('./GothicPortraitLayout'));

export interface LayoutConfig {
  id: string;
  name: string;
  description: string;
  component: ComponentType<LayoutProps>;
  requiresPremium: boolean;
  isDefault?: boolean;
  category: 'Classic' | 'Modern' | 'Creative' | 'Minimal' | 'Retro' | 'Temporada';
  tags: string[];
  /** Kill-switch: false = deja de ofrecerse en el selector Y getLayout() lo trata como
   * "no encontrado", cayendo a Classic — así un usuario que ya lo tenía elegido vuelve al
   * default automáticamente, sin tocar su doc en Firestore, en cuanto se apaga aquí. */
  enabled?: boolean;
  /** Badge "Por tiempo limitado" en el selector — puramente de marketing/UI. */
  limitedTime?: boolean;
  /** El layout tiene un look fijo: ignora el color de acento, el fondo y la fuente que el
   * usuario eligió en el resto del formulario. Se muestra como aviso en el panel de
   * detalle del selector — antes vivía como texto libre dentro de `description`. */
  fixedLook?: boolean;
  /** Fecha ISO (YYYY-MM-DD) a partir de la cual el layout aparece en el SELECTOR para que
   * los usuarios lo puedan elegir. Antes de esa fecha sigue oculto de la lista — pero solo
   * ahí: si alguien ya tiene ese id guardado en su doc (por ejemplo, se sembró a mano para
   * probarlo antes del lanzamiento), su perfil lo sigue renderizando normal. El código ya
   * vive desplegado en producción desde hoy, no hace falta acordarse de tocar nada el día
   * que se active — solo se le abre la puerta al selector. Se evalúa con la hora del
   * SERVIDOR (Date.now()), no la del navegador de cada visitante. */
  availableFrom?: string;
}

// Filtro del SELECTOR únicamente (fecha + kill-switch) — no se usa para decidir qué
// renderiza un perfil que ya tiene el id guardado, ver getLayout() más abajo.
function isSelectableInPicker(l: LayoutConfig): boolean {
  if (l.enabled === false) return false;
  if (l.availableFrom && new Date() < new Date(l.availableFrom)) return false;
  return true;
}

export const LAYOUT_REGISTRY: LayoutConfig[] = [
  {
    id: 'classic',
    name: 'Classic',
    description: 'El diseño original. Limpio, elegante y 100% centrado en tus enlaces.',
    component: ClassicLayout,
    requiresPremium: false,
    isDefault: true,
    category: 'Classic',
    tags: ['Standard', 'Clean'],
  },
  {
    id: 'sidebar',
    name: 'Sidebar',
    description: 'Perfil lateral con información de contacto directa y clara.',
    component: SidebarLayout,
    requiresPremium: true,
    category: 'Classic',
    tags: ['Professional', 'Direct'],
  },
  {
    id: 'minimal',
    name: 'Minimal',
    description: 'Menos es más. Fotografía inmersiva a pantalla completa en móviles.',
    component: MinimalLayout,
    requiresPremium: true,
    category: 'Minimal',
    tags: ['Full-bleed', 'Photo'],
  },
  {
    id: 'card-stack',
    name: 'Card Stack',
    description: 'Estructura modular. Cada bloque de contenido vive en su propia tarjeta.',
    component: CardStackLayout,
    requiresPremium: true,
    category: 'Modern',
    tags: ['Structured', 'Clear'],
  },
  {
    id: 'hero-banner',
    name: 'Hero Banner',
    description: 'Portada destacada con tu avatar superpuesto para máximo impacto visual.',
    component: HeroBannerLayout,
    requiresPremium: true,
    category: 'Modern',
    tags: ['Visual', 'Impactful'],
  },
  {
    id: 'beats',
    name: 'Beats',
    description: 'Paneles redondeados y segmentados. Perfecto para artistas y creadores.',
    component: BeatsLayout,
    requiresPremium: true,
    category: 'Modern',
    tags: ['Segmented', 'Dynamic'],
  },
  {
    id: 'grid',
    name: 'Grid',
    description: 'Inspirado en iOS. Cuadrícula de apps ideal para links cortos e íconos.',
    component: GridLayout,
    requiresPremium: true,
    category: 'Creative',
    tags: ['App-like', 'Compact'],
  },
  {
    id: 'bubble',
    name: 'Bubble',
    description: 'Diseño amigable y divertido con botones en forma de píldora interactiva.',
    component: BubbleLayout,
    requiresPremium: true,
    category: 'Creative',
    tags: ['Playful', 'Rounded'],
  },
  {
    id: 'split',
    name: 'Split',
    description: 'División audaz en dos paneles de color. Ideal para marcas atrevidas.',
    component: SplitLayout,
    requiresPremium: true,
    category: 'Creative',
    tags: ['Dual-tone', 'Bold'],
  },
  {
    id: 'crt-80s',
    name: 'CRT Retro',
    description: 'Computadora de los 80s: chasis crema, "teclas" con bisel y tu foto como pantalla CRT.',
    component: Crt80sLayout,
    requiresPremium: true,
    category: 'Retro',
    tags: ['Retro'],
    enabled: true,
    limitedTime: true,
    fixedLook: true,
  },
  {
    id: 'halloween-light',
    name: 'Halloween Claro',
    description: 'Fondo naranja con acento morado, marco de retrato antiguo, telaraña en la esquina y fantasma animado.',
    component: HalloweenLightLayout,
    requiresPremium: true,
    category: 'Temporada',
    tags: ['Temporada'],
    enabled: true,
    availableFrom: '2026-09-20',
    fixedLook: true,
  },
  {
    id: 'halloween-dark',
    name: 'Halloween Oscuro',
    description: 'Fondo morado con acento naranja — los mismos elementos que el claro, colores invertidos.',
    component: HalloweenDarkLayout,
    requiresPremium: true,
    category: 'Temporada',
    tags: ['Temporada'],
    enabled: true,
    availableFrom: '2026-09-20',
    fixedLook: true,
  },
  {
    id: 'spectral-stripes',
    name: 'Rayas Espectrales',
    description: 'Rayas diagonales sutiles, verde neón y morado sobre negro, marco circular brillante, tipografía de horror vintage.',
    component: SpectralStripesLayout,
    requiresPremium: true,
    category: 'Temporada',
    tags: ['Temporada'],
    enabled: true,
    availableFrom: '2026-09-20',
    fixedLook: true,
  },
  {
    id: 'gothic-portrait',
    name: 'Retrato Gótico',
    description: 'Placa grabada en oro viejo sobre negro cálido, retrato ovalado en madera oscura, tipografía gótica seria — sin íconos ni animación.',
    component: GothicPortraitLayout,
    requiresPremium: true,
    category: 'Temporada',
    tags: ['Temporada'],
    enabled: true,
    availableFrom: '2026-10-25',
    fixedLook: true,
  },
];

// Orden de presentación de categorías en el selector, y su etiqueta visible en español.
export const CATEGORY_ORDER: LayoutConfig['category'][] = [
  'Classic', 'Modern', 'Creative', 'Minimal', 'Retro', 'Temporada',
];

export const CATEGORY_LABELS: Record<LayoutConfig['category'], string> = {
  Classic: 'Clásicos',
  Modern: 'Modernos',
  Creative: 'Creativos',
  Minimal: 'Minimalistas',
  Retro: 'Retro',
  Temporada: 'De temporada',
};

// Layouts que el USUARIO puede elegir hoy en el selector — filtra apagados
// (enabled:false) Y los que aún no llegan a su fecha de activación (availableFrom). Usar
// esta lista (no LAYOUT_REGISTRY directo) en cualquier UI donde el usuario elige un layout.
export const AVAILABLE_LAYOUTS = LAYOUT_REGISTRY.filter(isSelectableInPicker);

// Agrupa AVAILABLE_LAYOUTS por categoría, en el orden de CATEGORY_ORDER, y omite las
// categorías sin ningún layout seleccionable (p. ej. "Temporada" antes de su
// availableFrom) — así la UI no tiene que armar el agrupado ni filtrar filas vacías.
export function getSelectableLayoutsByCategory(): { category: LayoutConfig['category']; label: string; layouts: LayoutConfig[] }[] {
  return CATEGORY_ORDER
    .map(category => ({
      category,
      label: CATEGORY_LABELS[category],
      layouts: AVAILABLE_LAYOUTS.filter(l => l.category === category),
    }))
    .filter(group => group.layouts.length > 0);
}

// Qué renderiza un perfil dado su id guardado. A propósito NO aplica availableFrom — un
// perfil sembrado a mano con un layout de temporada antes de su fecha de lanzamiento (para
// probarlo) debe seguir viéndose normal; solo enabled:false (el kill-switch real) lo tira a
// Classic.
export function getLayout(id?: string | null): LayoutConfig {
  const found = LAYOUT_REGISTRY.find(l => l.id === id && l.enabled !== false);
  return found ?? LAYOUT_REGISTRY[0];
}
